import React, { useCallback, useEffect, useMemo, useState } from "react";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePermission } from "../../../context/PermissionContext";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  FaArrowDown,
  FaArrowUp,
  FaCheckCircle,
  FaExternalLinkAlt,
  FaEye,
  FaLayerGroup,
  FaSave,
  FaSearch,
  FaSlidersH,
  FaTags,
} from "react-icons/fa";
import { extractObjectId, formatDateTime, htmlToText } from "../../../utils/utils";
import "./PageManagement.css";

const SECTION_LABELS = {
  hero: "Hero Banner",
  recommendedArtists: "Recommended Artists",
  trendingPlaylist: "Trending Playlist",
  trendingArtists: "Trending Artists",
  topExploreAlbums: "Top Explore Albums",
  musicMasterQuiz: "Music Master Quiz",
  recommendedSongs: "Recommended Songs",
  youtubeVideos: "YouTube Videos",
  trendingPodcast: "Trending Podcast",
  mostPopularSongs: "Most Popular Songs",
  news: "News",
  followUs: "Follow Us",
};

const DEFAULT_HERO = {
  showBanner: true,
  showNewReleases: true,
  explicitNewsArticleId: null,
  fallbackToLatestGenreNews: true,
  fallbackToHomepageHero: true,
};

function normalizeGenreValue(value) {
  if (!value) return "";
  return decodeURIComponent(String(value))
    .trim()
    .replace(/\\+$/, "")
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function normalizeNewsGenres(rawValue) {
  if (!rawValue) return [];

  if (Array.isArray(rawValue)) {
    return rawValue.flatMap((item) => normalizeNewsGenres(item));
  }

  if (typeof rawValue === "string") {
    return rawValue
      .split(",")
      .map((item) => normalizeGenreValue(item))
      .filter(Boolean);
  }

  return [];
}

function articleMatchesTarget(newsItem, target) {
  if (!target || target.pageType !== "genre") {
    return true;
  }

  const targetGenre = normalizeGenreValue(target.genreSlug);
  if (!targetGenre) {
    return false;
  }

  const genres = [
    ...normalizeNewsGenres(newsItem?.genre),
    ...normalizeNewsGenres(newsItem?.genres),
  ];

  return genres.includes(targetGenre);
}

function sanitizeConfigData(data = {}) {
  const sections = Array.isArray(data.sections)
    ? [...data.sections]
        .map((section, index) => ({
          id: section.id,
          enabled: typeof section.enabled === "boolean" ? section.enabled : true,
          order: typeof section.order === "number" ? section.order : index,
        }))
        .sort((left, right) => left.order - right.order)
        .map((section, index) => ({ ...section, order: index }))
    : [];

  return {
    sections,
    hero: {
      ...DEFAULT_HERO,
      ...(data.hero || {}),
      explicitNewsArticleId: data?.hero?.explicitNewsArticleId || null,
    },
  };
}

function buildSavePayload(target, draftConfig) {
  return {
    pageType: target.pageType,
    ...(target.pageType === "genre" ? { genreSlug: target.genreSlug } : {}),
    sections: draftConfig.sections.map((section, index) => ({
      id: section.id,
      enabled: section.enabled,
      order: index,
    })),
    hero: {
      showBanner: draftConfig.hero.showBanner,
      showNewReleases: draftConfig.hero.showNewReleases,
      explicitNewsArticleId: draftConfig.hero.explicitNewsArticleId || null,
      fallbackToLatestGenreNews: draftConfig.hero.fallbackToLatestGenreNews,
      fallbackToHomepageHero: draftConfig.hero.fallbackToHomepageHero,
    },
  };
}

function getTargetKey(target) {
  return `${target.pageType}:${target.genreSlug || ""}`;
}

function getPublicSiteBaseUrl() {
  const explicitUrl =
    process.env.REACT_APP_SITE_URL ||
    process.env.REACT_APP_PUBLIC_SITE_URL ||
    process.env.REACT_APP_PUBLIC_BASE_URL;

  if (explicitUrl) {
    return explicitUrl.replace(/\/+$/, "");
  }

  if (typeof window !== "undefined") {
    if (window.location.hostname === "localhost") {
      return "http://localhost:3000";
    }

    return "https://lyricsweb.com";
  }

  return process.env.NODE_ENV === "production"
    ? "https://lyricsweb.com"
    : "http://localhost:3000";
}

function formatPreviewPath(path) {
  if (!path) return "/";

  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
}

const PageManagement = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSuperAdmin, hasAnyPermission } = usePermission();

  const canAccessPageManagement =
    isSuperAdmin() ||
    hasAnyPermission([
      "genre:view",
      "genre:edit",
      "news:view",
      "news:edit",
    ]);

  const [targets, setTargets] = useState([]);
  const [newsItems, setNewsItems] = useState([]);
  const [selectedTargetKey, setSelectedTargetKey] = useState("");
  const [loadedTargetKey, setLoadedTargetKey] = useState("");
  const [targetSearch, setTargetSearch] = useState("");
  const [heroSearch, setHeroSearch] = useState("");
  const [draftConfig, setDraftConfig] = useState({
    sections: [],
    hero: DEFAULT_HERO,
  });
  const [savedPayload, setSavedPayload] = useState(null);
  const [configMeta, setConfigMeta] = useState(null);
  const [isLoadingTargets, setIsLoadingTargets] = useState(true);
  const [isLoadingConfig, setIsLoadingConfig] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");

  const pageTypeParam = searchParams.get("pageType") || "homepage";
  const genreSlugParam = searchParams.get("genreSlug") || null;

  const filteredTargets = useMemo(() => {
    const query = targetSearch.trim().toLowerCase();
    if (!query) return targets;
    return targets.filter((target) =>
      [target.label, target.genreSlug, target.pageType]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [targetSearch, targets]);

  const selectedTarget = useMemo(
    () => targets.find((target) => getTargetKey(target) === selectedTargetKey) || null,
    [selectedTargetKey, targets],
  );

  const availableHeroArticles = useMemo(() => {
    const filtered = newsItems.filter((newsItem) =>
      articleMatchesTarget(newsItem, selectedTarget),
    );

    if (!heroSearch.trim()) {
      return filtered;
    }

    const query = heroSearch.trim().toLowerCase();
    return filtered.filter((newsItem) =>
      [newsItem?.title, htmlToText(newsItem?.description)]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [heroSearch, newsItems, selectedTarget]);

  const heroArticleOptions = useMemo(() => {
    const selectedArticleId = draftConfig.hero.explicitNewsArticleId;
    if (!selectedArticleId) {
      return availableHeroArticles;
    }

    const alreadyIncluded = availableHeroArticles.some(
      (newsItem) => extractObjectId(newsItem?._id) === selectedArticleId,
    );

    if (alreadyIncluded) {
      return availableHeroArticles;
    }

    const selectedArticle = newsItems.find(
      (newsItem) => extractObjectId(newsItem?._id) === selectedArticleId,
    );

    return selectedArticle
      ? [selectedArticle, ...availableHeroArticles]
      : availableHeroArticles;
  }, [availableHeroArticles, draftConfig.hero.explicitNewsArticleId, newsItems]);

  const dirty = useMemo(() => {
    if (!selectedTarget || !savedPayload) return false;
    return (
      JSON.stringify(buildSavePayload(selectedTarget, draftConfig)) !==
      JSON.stringify(savedPayload)
    );
  }, [draftConfig, savedPayload, selectedTarget]);

  const syncSearchParams = useCallback(
    (target) => {
      const nextParams = new URLSearchParams();
      nextParams.set("pageType", target.pageType);
      if (target.pageType === "genre" && target.genreSlug) {
        nextParams.set("genreSlug", target.genreSlug);
      }
      setSearchParams(nextParams, { replace: true });
    },
    [setSearchParams],
  );

  const fetchPageConfig = useCallback(
    async (target) => {
      if (!target) return;

      setIsLoadingConfig(true);
      setLoadError("");

      const response = await allAPiServicesCall.getPageConfig(
        {
          pageType: target.pageType,
          ...(target.pageType === "genre" ? { genreSlug: target.genreSlug } : {}),
        },
        getAuthConfig(),
        navigate,
      );

      if (!response?.data) {
        setLoadError("Failed to load page configuration.");
        setIsLoadingConfig(false);
        return;
      }

      const sanitized = sanitizeConfigData(response.data);
      setDraftConfig(sanitized);
      setSavedPayload(buildSavePayload(target, sanitized));
      setConfigMeta(response.meta || null);
      setLastUpdatedAt(response.data?.updatedAt || response.data?.createdAt || "");
      setLoadedTargetKey(getTargetKey(target));
      setIsLoadingConfig(false);
    },
    [navigate],
  );

  useEffect(() => {
    if (!canAccessPageManagement) {
      setIsLoadingTargets(false);
      return;
    }

    const loadInitialData = async () => {
      setIsLoadingTargets(true);
      setLoadError("");

      const [targetsResponse, newsResponse] = await Promise.all([
        allAPiServicesCall.getPageConfigTargets(getAuthConfig(), navigate),
        allAPiServicesCall.getNews({}, getAuthConfig(), navigate),
      ]);

      const fetchedTargets = Array.isArray(targetsResponse?.data)
        ? targetsResponse.data
        : [];

      setTargets(fetchedTargets);
      setNewsItems(Array.isArray(newsResponse?.data) ? newsResponse.data : []);

      if (fetchedTargets.length === 0) {
        setLoadError("No page management targets were returned by the API.");
        setIsLoadingTargets(false);
        return;
      }

      setIsLoadingTargets(false);
    };

    loadInitialData();
  }, [canAccessPageManagement, navigate]);

  useEffect(() => {
    if (!targets.length) {
      return;
    }

    const nextTarget =
      targets.find((target) => {
        if (pageTypeParam !== target.pageType) return false;
        return pageTypeParam === "genre"
          ? normalizeGenreValue(target.genreSlug) === normalizeGenreValue(genreSlugParam)
          : true;
      }) || targets[0];

    const nextTargetKey = getTargetKey(nextTarget);

    if (selectedTargetKey !== nextTargetKey) {
      setSelectedTargetKey(nextTargetKey);
    }

    if (
      loadedTargetKey !== nextTargetKey &&
      !isLoadingTargets &&
      !isLoadingConfig
    ) {
      syncSearchParams(nextTarget);
      fetchPageConfig(nextTarget);
    }
  }, [
    fetchPageConfig,
    genreSlugParam,
    isLoadingConfig,
    isLoadingTargets,
    loadedTargetKey,
    pageTypeParam,
    selectedTargetKey,
    syncSearchParams,
    targets,
  ]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);

  const handleTargetSelect = async (event) => {
    const nextTargetKey = event.target.value;
    if (nextTargetKey === selectedTargetKey) return;

    if (dirty) {
      const shouldContinue = window.confirm(
        "You have unsaved changes for this page target. Switch targets and discard them?",
      );
      if (!shouldContinue) {
        return;
      }
    }

    const nextTarget = targets.find((target) => getTargetKey(target) === nextTargetKey);
    if (!nextTarget) return;

    setSelectedTargetKey(nextTargetKey);
    syncSearchParams(nextTarget);
    await fetchPageConfig(nextTarget);
  };

  const moveSection = (sectionId, direction) => {
    setDraftConfig((previous) => {
      const sections = [...previous.sections];
      const index = sections.findIndex((section) => section.id === sectionId);
      if (index < 0) return previous;

      const nextIndex = direction === "up" ? index - 1 : index + 1;
      if (nextIndex < 0 || nextIndex >= sections.length) {
        return previous;
      }

      [sections[index], sections[nextIndex]] = [sections[nextIndex], sections[index]];

      return {
        ...previous,
        sections: sections.map((section, nextOrder) => ({
          ...section,
          order: nextOrder,
        })),
      };
    });
  };

  const toggleSection = (sectionId, enabled) => {
    setDraftConfig((previous) => ({
      ...previous,
      sections: previous.sections.map((section) =>
        section.id === sectionId ? { ...section, enabled } : section,
      ),
    }));
  };

  const updateHeroValue = (key, value) => {
    setDraftConfig((previous) => ({
      ...previous,
      hero: {
        ...previous.hero,
        [key]: value,
      },
    }));
  };

  const handleSave = async () => {
    if (!selectedTarget) return;

    setIsSaving(true);
    const payload = buildSavePayload(selectedTarget, draftConfig);
    const response = await allAPiServicesCall.savePageConfig(
      payload,
      getAuthConfig(),
      navigate,
    );

    if (response?.data) {
      const sanitized = sanitizeConfigData(response.data);
      setDraftConfig(sanitized);
      setSavedPayload(buildSavePayload(selectedTarget, sanitized));
      setConfigMeta(response.meta || null);
      setLastUpdatedAt(response.data?.updatedAt || response.data?.createdAt || "");
      setLoadedTargetKey(getTargetKey(selectedTarget));
      setTargets((previous) =>
        previous.map((target) =>
          getTargetKey(target) === getTargetKey(selectedTarget)
            ? { ...target, hasStoredConfig: true, previewUrl: response?.meta?.previewUrl || target.previewUrl }
            : target,
        ),
      );
    }

    setIsSaving(false);
  };

  const handleReset = () => {
    if (!selectedTarget || !savedPayload) return;

    const shouldReset = window.confirm(
      "Reset your unsaved changes and restore the saved page configuration?",
    );

    if (!shouldReset) return;

    fetchPageConfig(selectedTarget);
  };

  const handlePreview = () => {
    const previewPath = configMeta?.previewUrl || selectedTarget?.previewUrl;
    if (!previewPath) return;

    window.open(`${getPublicSiteBaseUrl()}${previewPath}`, "_blank", "noopener,noreferrer");
  };

  if (!canAccessPageManagement) {
    return (
      <CommonLayout>
        <Container className="mt-5 pt-5">
          <Alert variant="warning" className="page-management-alert">
            You do not have permission to access Page Management.
          </Alert>
        </Container>
      </CommonLayout>
    );
  }

  return (
    <CommonLayout>
      <div className="page-management-page">
        <Container className="page-management-container mt-5 pt-5">
          <div className="page-management-header">
            <div className="page-management-header-main">
              <div className="page-management-header-icon">
                <FaLayerGroup />
              </div>
              <div>
                <h1>Page Management</h1>
                <p>
                  Edit homepage and genre-page layout, hero behavior, and live
                  publishing targets from one place.
                </p>
              </div>
            </div>
            <div className="page-management-header-actions">
              <Button
                variant="outline-dark"
                className="page-management-preview-btn"
                onClick={handlePreview}
                disabled={!selectedTarget || isLoadingConfig}
              >
                <FaEye /> Preview
              </Button>
              <Button
                className="page-management-save-btn"
                onClick={handleSave}
                disabled={!selectedTarget || isLoadingConfig || isSaving}
              >
                {isSaving ? <Spinner animation="border" size="sm" /> : <FaSave />}
                Save &amp; Publish
              </Button>
            </div>
          </div>

          {loadError ? (
            <Alert variant="danger" className="page-management-alert">
              {loadError}
            </Alert>
          ) : null}

          <Row className="g-4">
            <Col xl={4}>
              <Card className="page-management-card">
                <Card.Body>
                  <div className="page-management-card-title">
                    <FaTags />
                    <span>Choose Page Target</span>
                  </div>

                  <InputGroup className="page-management-search">
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      value={targetSearch}
                      onChange={(event) => setTargetSearch(event.target.value)}
                      placeholder="Search homepage or genre..."
                    />
                  </InputGroup>

                  {isLoadingTargets ? (
                    <div className="page-management-loading">
                      <Spinner animation="border" size="sm" />
                      <span>Loading targets...</span>
                    </div>
                  ) : (
                    <Form.Select
                      value={selectedTargetKey}
                      onChange={handleTargetSelect}
                      className="page-management-select"
                    >
                      {filteredTargets.map((target) => (
                        <option key={getTargetKey(target)} value={getTargetKey(target)}>
                          {target.label}
                          {target.hasStoredConfig ? " (Saved)" : " (Default)"}
                        </option>
                      ))}
                    </Form.Select>
                  )}

                  <div className="page-management-target-meta">
                    <div>
                      <span className="label">Editing</span>
                      <strong>{configMeta?.targetSummary?.label || selectedTarget?.label || "Select a target"}</strong>
                    </div>
                    <div>
                      <span className="label">Source</span>
                      <Badge bg={configMeta?.configSource === "stored" ? "dark" : "secondary"}>
                        {configMeta?.configSource || "default"}
                      </Badge>
                    </div>
                    <div>
                      <span className="label">Preview</span>
                      <code>
                        {formatPreviewPath(configMeta?.previewUrl || selectedTarget?.previewUrl)}
                      </code>
                    </div>
                    <div>
                      <span className="label">Last Updated</span>
                      <strong>
                        {lastUpdatedAt
                          ? formatDateTime(lastUpdatedAt, "Saved config loaded")
                          : "Using default config"}
                      </strong>
                    </div>
                  </div>

                  <Alert variant="light" className="page-management-scope-banner">
                    <strong>
                      {selectedTarget?.pageType === "homepage"
                        ? "Homepage edits only affect the homepage."
                        : `Genre edits only affect ${selectedTarget?.label || "that genre page"}.`}
                    </strong>
                    <div className="page-management-scope-note">
                      Save publishes live immediately and clears the relevant page-config
                      and banner cache on the backend.
                    </div>
                  </Alert>

                  {dirty ? (
                    <div className="page-management-dirty">
                      <FaCheckCircle />
                      <span>You have unsaved changes for this page target.</span>
                    </div>
                  ) : null}
                </Card.Body>
              </Card>
            </Col>

            <Col xl={8}>
              <Card className="page-management-card">
                <Card.Body>
                  <div className="page-management-card-title">
                    <FaSlidersH />
                    <span>Page Config Editor</span>
                  </div>

                  {isLoadingConfig ? (
                    <div className="page-management-loading page-management-loading-lg">
                      <Spinner animation="border" />
                      <span>Loading page configuration...</span>
                    </div>
                  ) : (
                    <>
                      <div className="page-management-section">
                        <div className="page-management-section-header">
                          <h3>Hero Settings</h3>
                          <p>Set homepage or genre hero behavior, then preview the saved page.</p>
                        </div>

                        <Row className="g-3">
                          <Col md={6}>
                            <Form.Check
                              type="switch"
                              label="Show hero banner"
                              checked={draftConfig.hero.showBanner}
                              onChange={(event) =>
                                updateHeroValue("showBanner", event.target.checked)
                              }
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Check
                              type="switch"
                              label="Show new releases beside hero"
                              checked={draftConfig.hero.showNewReleases}
                              onChange={(event) =>
                                updateHeroValue("showNewReleases", event.target.checked)
                              }
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Check
                              type="switch"
                              label="Fallback to latest genre article"
                              checked={draftConfig.hero.fallbackToLatestGenreNews}
                              disabled={selectedTarget?.pageType !== "genre"}
                              onChange={(event) =>
                                updateHeroValue(
                                  "fallbackToLatestGenreNews",
                                  event.target.checked,
                                )
                              }
                            />
                          </Col>
                          <Col md={6}>
                            <Form.Check
                              type="switch"
                              label="Fallback to homepage hero"
                              checked={draftConfig.hero.fallbackToHomepageHero}
                              onChange={(event) =>
                                updateHeroValue(
                                  "fallbackToHomepageHero",
                                  event.target.checked,
                                )
                              }
                            />
                          </Col>
                        </Row>

                        <div className="page-management-hero-picker">
                          <div className="page-management-picker-header">
                            <div>
                              <h4>Explicit Hero Article</h4>
                              <p>
                                {selectedTarget?.pageType === "genre"
                                  ? "Only articles tagged to this genre are shown here."
                                  : "Homepage can choose from all live articles."}
                              </p>
                            </div>
                          </div>

                          <InputGroup className="page-management-search">
                            <InputGroup.Text>
                              <FaSearch />
                            </InputGroup.Text>
                            <Form.Control
                              value={heroSearch}
                              onChange={(event) => setHeroSearch(event.target.value)}
                              placeholder="Search hero articles by title..."
                            />
                          </InputGroup>

                          <Form.Select
                            value={draftConfig.hero.explicitNewsArticleId || ""}
                            onChange={(event) =>
                              updateHeroValue(
                                "explicitNewsArticleId",
                                event.target.value || null,
                              )
                            }
                          >
                            <option value="">No explicit hero article</option>
                            {heroArticleOptions.map((newsItem) => {
                              const newsId = extractObjectId(newsItem?._id);
                              return (
                                <option key={newsId} value={newsId}>
                                  {newsItem?.title}
                                </option>
                              );
                            })}
                          </Form.Select>
                        </div>
                      </div>

                      <div className="page-management-section">
                        <div className="page-management-section-header">
                          <h3>Section Visibility &amp; Order</h3>
                          <p>Reorder sections and choose which ones appear on the selected page target.</p>
                        </div>

                        <div className="page-management-section-list">
                          {draftConfig.sections.map((section, index) => (
                            <div key={section.id} className="page-management-section-row">
                              <div className="page-management-section-info">
                                <Badge bg="secondary">#{index + 1}</Badge>
                                <div>
                                  <strong>{SECTION_LABELS[section.id] || section.id}</strong>
                                  <div className="page-management-section-id">{section.id}</div>
                                </div>
                              </div>

                              <div className="page-management-section-actions">
                                <Form.Check
                                  type="switch"
                                  label={section.enabled ? "Visible" : "Hidden"}
                                  checked={section.enabled}
                                  onChange={(event) =>
                                    toggleSection(section.id, event.target.checked)
                                  }
                                />
                                <div className="page-management-order-buttons">
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => moveSection(section.id, "up")}
                                    disabled={index === 0}
                                  >
                                    <FaArrowUp />
                                  </Button>
                                  <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={() => moveSection(section.id, "down")}
                                    disabled={index === draftConfig.sections.length - 1}
                                  >
                                    <FaArrowDown />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="page-management-footer">
                        <Button
                          variant="outline-secondary"
                          onClick={handleReset}
                          disabled={!dirty || isSaving}
                        >
                          Reset Unsaved Changes
                        </Button>
                        <div className="page-management-footer-actions">
                          <Button
                            variant="outline-dark"
                            onClick={handlePreview}
                            disabled={isSaving}
                          >
                            <FaExternalLinkAlt /> Preview Saved Page
                          </Button>
                        <Button
                          className="page-management-save-btn"
                          onClick={handleSave}
                          disabled={isSaving || !selectedTarget}
                        >
                          {isSaving ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                              <FaSave />
                            )}
                            Save &amp; Publish
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </CommonLayout>
  );
};

export default PageManagement;
